import {inject, Injectable} from "@angular/core";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {catchError, map, Observable, shareReplay, throwError} from "rxjs";
import {FeatureToggleModel} from "../../../interfaces";
import {AuthService} from "../auth/auth.service";
import {
  FeatureToggleApiModel,
  FeatureToggleApiResponse,
  FeatureToggleDetailResponse,
  FeatureToggleHistoryResponse,
  UpdateConfigResponse,
  UpdateSubscriberResponse
} from "../../../interfaces/api/feature-toggle-api.interface";
import {mapFeatureToggleFromApi, mapFeatureToggleFromApiResponse} from "../../../mapping";
import {FeatureTogglePermissionsService} from "../feature-toggle-permissions.service";
import {Environment, ToastSeverityEnum} from "../../../enums";
import {ToastsService} from "../toasts/toasts.service";

@Injectable({
  providedIn: "root"
})
export class FeatureTogglesService {
  private readonly httpClient = inject(HttpClient);
  private readonly permissionsService = inject(FeatureTogglePermissionsService);
  private readonly toastsService = inject(ToastsService);
  private readonly authService = inject(AuthService);

  // API Constants
  private readonly API_SUBTYPE_LOCAL = "2"; // Local/CustomerCountry feature toggles

  // Recommended State Values
  private readonly RECOMMENDED_STATE_UNSET = "-1";
  private readonly RECOMMENDED_STATE_DISABLED = "0";
  private readonly RECOMMENDED_STATE_ENABLED = "1";

  // Feature Toggle Status Values
  private readonly STATUS_DISABLED = "0";
  private readonly STATUS_ENABLED = "1";

  // Cache
  private cachedFeatures$: Record<string, Observable<FeatureToggleModel[]>> = {};
  private cacheTimestamps: Record<string, number> = {};
  private readonly CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

  public getFeatureTogglesByEnvironment(environment: string): Observable<FeatureToggleModel[]> {
    const now = Date.now();
    const isCacheValid =
      this.cachedFeatures$[environment] &&
      this.cacheTimestamps[environment] &&
      now - this.cacheTimestamps[environment] < this.CACHE_DURATION_MS;

    if (!isCacheValid) {
      const apiBaseUrl = this.getApiBaseUrlForEnvironment(environment);

      const envPath = this.isLocalDevelopment() ? `/${environment}` : "";
      const url = `${apiBaseUrl}/featuretoggles${envPath}/1.0/list/?subtype=${this.API_SUBTYPE_LOCAL}`;

      this.cachedFeatures$[environment] = this.httpClient.get<FeatureToggleApiResponse>(url).pipe(
        map(response => {
          return mapFeatureToggleFromApiResponse(response);
        }),
        catchError(error => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to load feature toggles for ${environment}. ${error.message || "Please try again."}`,
            timeout: 5000
          });
          return throwError(() => error);
        }),
        shareReplay({bufferSize: 1, refCount: true})
      );
      this.cacheTimestamps[environment] = now;
    }

    return this.cachedFeatures$[environment];
  }

  /**
   * Maps the environment parameter to the correct API base URL
   * Handles local development proxy routing vs deployed environment URLs
   * @param environment - integration, staging, or production
   * @returns The appropriate API base URL (empty string for local development)
   */
  private getApiBaseUrlForEnvironment(environment: string): string {
    // Check if running locally (localhost or 127.0.0.1)
    const isLocalDevelopment =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" || window.location.hostname === "";

    // If running locally, use empty string because the proxy handles the /featuretoggles prefix
    if (isLocalDevelopment) {
      return "";
    }

    const environmentUrls: Record<string, string> = {
      integration: "https://cms-d.corp.trumpf.com/api/default",
      staging: "https://trumpf.com.cms.qa.tru/api/default",
      production: "https://trumpf.com.cms.tru/api/default"
    };

    const url = environmentUrls[environment];
    if (!url) {
      throw new Error(`Unknown environment: '${environment}'. Expected: integration, staging, or production`);
    }

    return url;
  }

  /**
   * Local Development Check
   */
  private isLocalDevelopment(): boolean {
    return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1" || window.location.hostname === "";
  }

  public getFeatureToggleById(environment: string, id: string): Observable<FeatureToggleModel | null> {
    const apiBaseUrl = this.getApiBaseUrlForEnvironment(environment);
    const envPath = this.isLocalDevelopment() ? `/${environment}` : "";
    const url = `${apiBaseUrl}/featuretoggles${envPath}/1.0/show/?feature=${id}`;

    return this.httpClient.get<FeatureToggleDetailResponse>(url).pipe(
      map(response => {
        if (response.payload?.feature) {
          return mapFeatureToggleFromApi(response.payload.feature);
        }
        return null;
      }),
      catchError(error => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to load feature toggle. ${error.message || "Please try again."}`,
          timeout: 5000
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Gets feature toggle details including both the transformed model and raw API data
   */
  public getFeatureToggleDetails(
    environment: string,
    id: string
  ): Observable<{model: FeatureToggleModel | null; apiData: FeatureToggleApiModel | null}> {
    const apiBaseUrl = this.getApiBaseUrlForEnvironment(environment);
    const envPath = this.isLocalDevelopment() ? `/${environment}` : "";
    const url = `${apiBaseUrl}/featuretoggles${envPath}/1.0/show/?feature=${id}`;

    return this.httpClient.get<FeatureToggleDetailResponse>(url).pipe(
      map(response => {
        if (response.payload?.feature) {
          return {
            model: mapFeatureToggleFromApi(response.payload.feature),
            apiData: response.payload.feature
          };
        }
        return {model: null, apiData: null};
      }),
      catchError(error => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to load feature toggle details. ${error.message || "Please try again."}`,
          timeout: 5000
        });
        return throwError(() => error);
      })
    );
  }

  public getFeatureToggleHistory(
    environment: string,
    featureId: string,
    country?: string
  ): Observable<FeatureToggleHistoryResponse> {
    const apiBaseUrl = this.getApiBaseUrlForEnvironment(environment);
    const envPath = this.isLocalDevelopment() ? `/${environment}` : "";
    let url = `${apiBaseUrl}/featuretoggles${envPath}/1.0/history/?feature=${featureId}`;
    if (country) {
      url += `&filter=${country}`;
    }

    return this.httpClient.get<FeatureToggleHistoryResponse>(url).pipe(
      catchError(error => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to load feature toggle history. ${error.message || "Please try again."}`,
          timeout: 5000
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Clears the cached feature toggles data, forcing a fresh fetch on next request
   * @param environment Optional - clear cache for specific environment, or all if not provided
   */
  public clearCache(environment?: string): void {
    if (environment) {
      delete this.cachedFeatures$[environment];
      delete this.cacheTimestamps[environment];
    } else {
      this.cachedFeatures$ = {};
      this.cacheTimestamps = {};
    }
  }

  /**
   * Checks if the current user has permission to modify the given environment
   * @param environment The environment to check
   * @returns Observable that throws error if permission denied, or completes if allowed
   */
  private checkModificationPermission(environment: string): void | never {
    if (!this.permissionsService.canModifyEnvironment(environment as Environment)) {
      const restrictionMessage = this.permissionsService.getRestrictionMessage(environment as Environment);
      const errorMessage = restrictionMessage || `Permission denied: Cannot modify ${environment} environment`;

      this.toastsService.addToast({
        severity: ToastSeverityEnum.Warning,
        message: errorMessage,
        timeout: 5000
      });

      throw new Error(errorMessage);
    }
  }

  /**
   * Builds FormData from feature toggle model updates
   * Converts camelCase model properties to snake_case API field names
   */
  private buildConfigFormData(updates: Partial<FeatureToggleModel>): FormData {
    const formData = new FormData();

    const fieldMapping: Record<string, string> = {
      displayName: "display_name",
      description: "description",
      owner: "owner",
      application: "application",
      additionalInformation: "additional_information",
      affects: "affects",
      icon: "icon"
    };

    Object.entries(fieldMapping).forEach(([modelKey, apiKey]) => {
      const value = updates[modelKey as keyof FeatureToggleModel];
      if (value !== undefined) {
        formData.append(apiKey, String(value));
      }
    });

    if (updates.recommendedState !== undefined) {
      let value: string;
      if (updates.recommendedState === null) {
        value = this.RECOMMENDED_STATE_UNSET;
      } else if (updates.recommendedState) {
        value = this.RECOMMENDED_STATE_ENABLED;
      } else {
        value = this.RECOMMENDED_STATE_DISABLED;
      }
      formData.append("recommended", value);
    }

    return formData;
  }

  /**
   * Updates feature toggle configuration
   * Sends data as FormData to: POST /api/default/featuretoggles/1.0/config/
   * @param environment - The target environment (integration, staging, production)
   * @param id - Feature toggle ID
   * @param updates - Partial model with fields to update
   * @returns Observable of updated feature toggle model or null
   */
  public updateFeatureToggleConfig(
    environment: string,
    id: string,
    updates: Partial<FeatureToggleModel>
  ): Observable<FeatureToggleModel | null> {
    try {
      this.checkModificationPermission(environment);
    } catch (error) {
      return throwError(() => error);
    }

    const apiBaseUrl = this.getApiBaseUrlForEnvironment(environment);
    const envPath = this.isLocalDevelopment() ? `/${environment}` : "";
    const url = `${apiBaseUrl}/featuretoggles${envPath}/1.0/config/`;
    const params = new HttpParams().set("feature", id);
    const formData = this.buildConfigFormData(updates);

    const headers = new HttpHeaders({
      Origin: window.location.origin
    });

    return this.httpClient.post<UpdateConfigResponse>(url, formData, {params, headers}).pipe(
      map(response => {
        this.clearCache(environment);

        this.toastsService.addToast({
          severity: ToastSeverityEnum.Success,
          message: "Configuration saved successfully",
          timeout: 3000
        });

        if (response.payload?.feature) {
          return mapFeatureToggleFromApi(response.payload.feature);
        }

        return null;
      }),
      catchError(error => {
        const errorMsg = error.error?.message || error.message || "Unknown error occurred";
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to save configuration: ${errorMsg}`,
          timeout: 5000
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates a specific subscriber's status (e.g., enable/disable for a country or globally)
   * POST /api/default/featuretoggles/1.0/updateSubscriber/
   * @param environment - The target environment
   * @param featureId - Feature toggle ID
   * @param subscriber - Subscriber identifier (e.g., "trumpf:cms", "trumpf:live")
   * @param enabled - Whether to enable (true) or disable (false)
   * @param countryCode - Optional country code to filter the update to a specific country
   * @returns Observable of success boolean
   */
  public updateSubscriberStatus(
    environment: string,
    featureId: string,
    subscriber: string,
    enabled: boolean,
    countryCode?: string
  ): Observable<boolean> {
    try {
      this.checkModificationPermission(environment);
    } catch (error) {
      return throwError(() => error);
    }

    const apiBaseUrl = this.getApiBaseUrlForEnvironment(environment);
    const envPath = this.isLocalDevelopment() ? `/${environment}` : "";
    const url = `${apiBaseUrl}/featuretoggles${envPath}/1.0/updateSubscriber/`;

    let params = new HttpParams()
      .set("feature", featureId)
      .set("subscriber", subscriber)
      .set("status", enabled ? this.STATUS_ENABLED : this.STATUS_DISABLED)
      .set("log_user", this.authService.getUserName() || "Unknown User");

    if (countryCode) {
      params = params.set("filter", countryCode);
    }

    const headers = new HttpHeaders({
      Origin: window.location.origin
    });

    return this.httpClient.post<UpdateSubscriberResponse>(url, null, {params, headers}).pipe(
      map(response => {
        if (response.status === "success" || response.status === "ok") {
          this.clearCache(environment);

          const locationMsg = countryCode ? ` for ${countryCode}` : "";
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Success,
            message: `Feature toggle ${enabled ? "enabled" : "disabled"}${locationMsg} successfully`,
            timeout: 3000
          });

          return true;
        }
        return false;
      }),
      catchError(error => {
        const errorMsg = error.error?.payload?.message || error.error?.message || error.message || "Unknown error occurred";
        const locationMsg = countryCode ? ` for ${countryCode}` : "";
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to update feature toggle${locationMsg}. ${errorMsg}`,
          timeout: 5000
        });
        return throwError(() => error);
      })
    );
  }
}
